import { createPortal } from "react-dom";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { RadioButton } from "primereact/radiobutton";
import { Tooltip } from "primereact/tooltip";
import AngleRightIcon from "@/assets/icons/tipo-angle-right.svg?react";
import ChevronDownIcon from "@/assets/icons/tipo-chevron-down.svg?react";
import InfoDominanteIcon from "@/assets/icons/tipo-info-dominante.svg?react";
import PlusIcon from "@/assets/icons/tipo-plus.svg?react";
import TrashIcon from "@/assets/icons/tipo-trash.svg?react";
import { Accordion, AccordionTab, Checkbox, Dropdown, FormField, actionTooltipProps } from "@/ds";
import {
  DOMINANTE_LABELS,
  ESCOPO_MACRO,
  ESCOPO_VAZIO,
  TIPOS_A11Y,
  TIPOS_LABELS,
} from "../../../nova-obra.constants";
import { TagColetor } from "../../../components/tag-coletor";
import { EscopoMacroSecao } from "../escopo-macro-secao";
import styles from "../../etapa-tipos-obra.module.scss";
import { CONTRATO_MAX_LENGTH } from "./tipo-obra-card.constants";
import type { TipoObraCardProps, TipoObraCardViewModel } from "./types-tipo-obra-card";

/** Chevron do cabeçalho (Figma `recolher`: botão de 32 com o ícone de 14); o `Accordion` o põe antes do título. */
const CHEVRON_ABERTO = () => (
  <span className={styles.chevron}>
    <ChevronDownIcon aria-hidden />
  </span>
);
const CHEVRON_RECOLHIDO = () => (
  <span className={styles.chevron}>
    <AngleRightIcon aria-hidden />
  </span>
);

/** Estado "Sem Escopo" (RN16). O detalhe do Escopo Macro é do "Épico Escopo Macro" (sem HU). */
function EscopoVazio() {
  return (
    <div className={styles.escopoVazio} data-testid="escopo-vazio">
      <div className={styles.escopoVazioTexto}>
        <p className={styles.escopoTitle}>{ESCOPO_VAZIO.title}</p>
        <p className={styles.escopoText}>{ESCOPO_VAZIO.text}</p>
      </div>
      <Button
        type="button"
        size="small"
        severity="secondary"
        icon={<PlusIcon aria-hidden />}
        label={ESCOPO_VAZIO.adicionar}
        disabled
      />
    </div>
  );
}

/** Card de um tipo de obra (Figma `tipo-obra/card`): cabeçalho recolhível + regras por código. */
export function TipoObraCardView(props: TipoObraCardProps & TipoObraCardViewModel) {
  const {
    removerLabel,
    idBase,
    infoRef,
    mobile,
    lixeiraRef,
    cabecalhoEl,
    registrarCabecalho,
    radioKey,
    tornarDominante,
    testIds,
    escolherCliente,
    alterarContrato,
    marcarInspecaoCasco,
    escolherInspecaoCasco,
    tipo,
    aberto,
    clientes,
    erroInspecao,
    dominante,
    prioridadeTexto,
    escopoMacro,
    onToggle,
    onRemover,
    ehDocagem,
    ehMobilizacao,
    inspecaoOptions,
  } = props;

  const cabecalho = (
    <>
      <div className={styles.cardTitulo} data-testid={testIds.cabecalho}>
        <TagColetor codigo={tipo.codigo} />
        <span className={styles.cardTitle}>{tipo.nome}</span>
        {dominante && (
          <span className={styles.seloDominante} data-testid={testIds.seloDominante}>
            {DOMINANTE_LABELS.selo}
          </span>
        )}
        <span className={styles.spacer} />
        <span className={styles.cardCount}>
          {escopoMacro ? ESCOPO_MACRO.quantidade(escopoMacro.total) : TIPOS_LABELS.semEscopo}
        </span>
      </div>
    </>
  );

  const lixeira = (
    <>
      {/* Botão `disabled` não dispara hover/toque: o tooltip (Figma 12407:329681; no 301 automático, texto da HU) fica no span. */}
      <span
        ref={lixeiraRef}
        className={styles.lixeira}
        data-testid={testIds.lixeira}
        tabIndex={tipo.automatico ? 0 : undefined}
      >
        <button
          type="button"
          className={styles.iconButton}
          aria-label={`${removerLabel} ${tipo.codigo}`}
          disabled={tipo.automatico}
          onClick={onRemover}
        >
          <TrashIcon aria-hidden />
        </button>
      </span>
      <Tooltip
        target={lixeiraRef}
        {...actionTooltipProps(
          tipo.automatico ? TIPOS_A11Y.removerTipoAutomatico : TIPOS_A11Y.tooltipRemoverTipo,
          {
            position: "left",
            className: `${styles.tooltipLixeira} ${tipo.automatico ? styles.tooltipLixeiraAutomatica : ""}`,
          },
        )}
      />
    </>
  );

  return (
    <>
      <Accordion
        className={`p-accordion-card ${styles.card}`}
        activeIndex={aberto ? 0 : null}
        onTabChange={() => onToggle()}
        collapseIcon={CHEVRON_ABERTO}
        expandIcon={CHEVRON_RECOLHIDO}
        data-testid={testIds.card}
      >
        <AccordionTab
          headerTemplate={cabecalho}
          pt={{
            header: { className: styles.cardHeader, ref: registrarCabecalho },
            headerAction: {
              "aria-label": `${aberto ? TIPOS_A11Y.recolher : TIPOS_A11Y.expandir} ${tipo.codigo} ${tipo.nome}`,
            },
          }}
        >
          {aberto && (
            <div className={styles.cardBody}>
              {!dominante && (
                <div
                  className={`${styles.banner} ${styles.bannerDominante}`}
                  data-testid={testIds.opcaoDominante}
                >
                  <FormField.Root labelPosition="right" classNames={{ label: styles.checkLabel }}>
                    <FormField.Label>{DOMINANTE_LABELS.opcao}</FormField.Label>
                    <FormField.Control>
                      <RadioButton
                        key={radioKey}
                        name={`${idBase}-dominante`}
                        checked={false}
                        onChange={tornarDominante}
                      />
                    </FormField.Control>
                  </FormField.Root>
                  <Tooltip
                    target={infoRef}
                    {...actionTooltipProps(prioridadeTexto, {
                      position: mobile ? "top" : "right",
                      className: styles.tooltipDominante,
                      // Figma 12393:380406: o balão desce a partir do ⓘ (seta a 18px do topo), não centraliza nele.
                      // `my`/`at` do Prime 10.9.8: com `at` "center" no y, o topo do balão = topo do ⓘ + metade da altura do ⓘ
                      // (7) + o offset de `my`. Para a seta (a 18px do topo do balão) apontar o centro do ⓘ, o topo do
                      // balão fica 18px acima desse centro: offset −18 (topo do balão = topo do ⓘ + 7 − 18 = −11).
                      ...(mobile
                        ? null
                        : {
                            my: "left top-18",
                            at: "right center",
                            pt: { arrow: { className: styles.tooltipSeta } },
                          }),
                    })}
                  />
                  <button
                    ref={infoRef}
                    type="button"
                    className={styles.hintDominante}
                    data-testid={testIds.hintDominante}
                    aria-label={DOMINANTE_LABELS.info}
                    aria-describedby={`${idBase}-prioridade`}
                  >
                    <InfoDominanteIcon aria-hidden />
                  </button>
                  <span id={`${idBase}-prioridade`} hidden>
                    {prioridadeTexto}
                  </span>
                </div>
              )}

              {ehMobilizacao && (
                <div className={styles.clienteContrato}>
                  <FormField.Root className={styles.fieldCliente}>
                    <FormField.Label>{TIPOS_LABELS.cliente}</FormField.Label>
                    <FormField.Control>
                      <Dropdown
                        value={tipo.cliente || null}
                        options={[...clientes]}
                        onChange={(event) => escolherCliente(event.value)}
                        placeholder={TIPOS_LABELS.clientePlaceholder}
                        data-testid={testIds.cliente}
                      />
                    </FormField.Control>
                  </FormField.Root>
                  <FormField.Root className={styles.fieldContrato}>
                    <FormField.Label>{TIPOS_LABELS.contrato}</FormField.Label>
                    <FormField.Control>
                      <InputText
                        value={tipo.contrato}
                        maxLength={CONTRATO_MAX_LENGTH}
                        placeholder={TIPOS_LABELS.contratoPlaceholder}
                        onChange={(event) => alterarContrato(event.target.value)}
                        className={styles.input}
                      />
                    </FormField.Control>
                  </FormField.Root>
                </div>
              )}

              {ehDocagem && (
                <div className={styles.banner}>
                  <FormField.Root labelPosition="right" classNames={{ label: styles.checkLabel }}>
                    <FormField.Label>{TIPOS_LABELS.inspecaoCasco}</FormField.Label>
                    <FormField.Control>
                      <Checkbox
                        checked={tipo.haveraInspecaoCasco}
                        onChange={(event) => marcarInspecaoCasco(event.checked)}
                      />
                    </FormField.Control>
                  </FormField.Root>
                  {tipo.haveraInspecaoCasco && (
                    <FormField.Root className={styles.inspecaoSelect} error={erroInspecao}>
                      <FormField.Control>
                        <Dropdown
                          value={tipo.inspecaoCasco}
                          options={inspecaoOptions}
                          onChange={(event) => escolherInspecaoCasco(event.value)}
                          placeholder={TIPOS_LABELS.inspecaoCascoPlaceholder}
                          ariaLabel={TIPOS_LABELS.inspecaoCascoPlaceholder}
                          data-testid={testIds.inspecao}
                        />
                      </FormField.Control>
                      <FormField.Error />
                    </FormField.Root>
                  )}
                </div>
              )}

              {escopoMacro ? <EscopoMacroSecao {...escopoMacro} /> : <EscopoVazio />}
            </div>
          )}
        </AccordionTab>
      </Accordion>
      {cabecalhoEl ? createPortal(lixeira, cabecalhoEl) : null}
    </>
  );
}
